/**
 * EntryDock — внесение / правка отчёта за сотрудника (задача 71 §5.3;
 * задача 77 §3.1; задача 79 §2.1/§2.2).
 *
 * Док владеет **контейнером** дневной оболочки и плашкой режима; тело —
 * единый `<TimesheetDayEditor>` (задача 79 §2.1), общий с backfill-страницей:
 * записи дня + табель (`declared_total_hours_half`) + единый финал: self-flow
 * подписывает отчёт, manager-flow подтверждает чужой (§1.1).
 *
 * Контейнер выбирается по `surface` (задача 79 §2.2 — 1-в-1 как `EntryEditorCore`):
 *   - `desktop-dock` (D6, широкий вьюпорт) — правый `<Sheet side=right>` 460px
 *     (`patterns.md §Drawer`);
 *   - `mobile` (M6, `/field/lead`) — fullscreen-корень `fixed inset-0` (как
 *     мобильная ветка редактора записи), оверлей-переход поверх консоли.
 * Density-токены `--entry-tap-*` каскадят из `data-entry-surface` на корне тела
 * (`TimesheetDayEditor`): крупные тач-таргеты на мобильном, компактные на доке.
 *
 * Плашка режима (файл 22 §4.9, `patterns.md §Рабочее место руководителя`) — по
 * `subject.isEdit`: caution «отчёта не было» при внесении за несдавшего / info
 * «уточнение отчёта сотрудника» при правке сданного.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { TimesheetDayEditor } from "@/components/approval/TimesheetDayEditor";

export interface EntryDockSubject {
  userId: number;
  name: string;
  /** Должность — вторичная строка контекст-шапки (как на backfill); идентичность поверхностей. */
  position?: string | null;
  /** false → «внести за несдавшего» (pre-fill табель нормой), true → правка существующего. */
  isEdit: boolean;
  /** true → субъект = сам залогиненный (офис «Ввод времени», 73 §2.3): свой
   *  заголовок «Ввод/Правка рабочего времени», без manager-плашки провенанса. */
  isSelf?: boolean;
}

interface EntryDockProps {
  subject: EntryDockSubject | null;
  date: string;
  weekStart: string;
  unitId: number | null;
  /** Поверхность: desktop-dock = правый Sheet 460px, mobile = fullscreen (§2.2). */
  surface?: "mobile" | "desktop-dock";
  /** false → фон остаётся интерактивным (недельная сетка переключает выбранный день). */
  modal?: boolean;
  /** Исключение из outside-dismiss для контролов, которые атомарно меняют содержимое дока. */
  onInteractOutside?: React.ComponentProps<typeof SheetContent>["onInteractOutside"];
  onClose: () => void;
}

export function EntryDock({
  subject,
  date,
  weekStart,
  unitId,
  surface = "desktop-dock",
  modal = true,
  onInteractOutside,
  onClose,
}: EntryDockProps): React.ReactElement | null {
  const { t } = useTranslation();

  const isSelf = subject?.isSelf ?? false;
  const title = isSelf
    ? subject?.isEdit
      ? t("timeApproval.dockSelfEditTitle")
      : t("timeApproval.dockSelfEnterTitle")
    : subject?.isEdit
      ? t("timeApproval.dockEditTitle")
      : t("timeApproval.dockEnterTitle");
  const clarify = subject?.isEdit
    ? t("timeApproval.dockEditClarify")
    : t("timeApproval.dockEnteredByLead");

  // Плашка режима — caution «отчёта не было» (внесение за несдавшего) / info
  // «уточнение отчёта» (правка сданного). `.tone-*` несёт bg+fg+border одной
  // парой (см. patterns.md §Рабочее место руководителя). При self-вводе (офис
  // «Ввод времени», 73 §2.3) провенанс не показываем — плашка про руководителя.
  const modeNotice = isSelf ? null : (
    <div className="px-4 pt-4">
      <div
        id="entry-dock-mode-notice"
        className={`flex items-center gap-2 rounded-[var(--radius-card)] px-3 py-2 text-xs ${
          subject?.isEdit ? "tone-info" : "tone-caution"
        }`}
      >
        <span aria-hidden="true">✎</span>
        <span>{clarify}</span>
      </div>
    </div>
  );

  const body =
    subject !== null ? (
      <div className="flex min-h-0 flex-1 flex-col">
        <TimesheetDayEditor
          key={`${subject.userId}:${date}`}
          userId={subject.userId}
          date={date}
          name={subject.name}
          position={subject.position}
          weekStart={weekStart}
          unitId={unitId}
          surface={surface}
          isSelf={isSelf}
          onFinalized={onClose}
        />
      </div>
    ) : null;

  // Мобильная ветка (M6): fullscreen-корень — оверлей-переход поверх консоли
  // (`patterns.md §Рабочее место руководителя`, M-3). Только при открытом
  // субъекте: на телефоне нет фонового Sheet, рендерим по факту.
  if (surface === "mobile") {
    if (subject === null) return null;
    return (
      <div
        className="fixed inset-0 z-40 flex flex-col bg-popover text-popover-foreground"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="approval-entry-dock"
        data-surface="mobile"
      >
        <div className="flex items-center justify-between gap-2 border-b border-[var(--rule-line)] px-4 py-3">
          <span className="text-base font-semibold text-[var(--fg-1)]">{title}</span>
          <button
            type="button"
            aria-label={t("timeApproval.composerCancel")}
            data-testid="approval-entry-dock-close"
            onClick={onClose}
            className="inline-flex w-9 shrink-0 items-center justify-center rounded-[var(--radius-button)] text-[var(--fg-3)]"
            style={{ minHeight: "var(--entry-tap-tertiary)" }}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        {modeNotice}
        {body}
      </div>
    );
  }

  // Desktop-dock (D6): правый Sheet 460px (`patterns.md §Drawer`).
  return (
    <Sheet open={subject !== null} modal={modal} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        modal={modal}
        style={{ width: 460, maxWidth: "100vw" }}
        className="!w-[460px] !max-w-[100vw] gap-0 p-0"
        data-testid="approval-entry-dock"
        data-surface="desktop-dock"
        onInteractOutside={onInteractOutside}
        // Описание диалога — видимая плашка режима (а не отдельный sr-only
        // дубль): иначе скринридер читает «отчёта не было · вносит руководитель»
        // дважды подряд (UI-аудит 85 §5.3). Radix довольствуется aria-describedby.
        // При self-вводе плашки нет → describedby снимаем (иначе Radix warn).
        aria-describedby={isSelf ? undefined : "entry-dock-mode-notice"}
      >
        <SheetTitle className="border-b border-[var(--rule-line)] px-4 py-3 text-base font-semibold">
          {title}
        </SheetTitle>

        {modeNotice}
        {body}
      </SheetContent>
    </Sheet>
  );
}
